// Pure numeric helpers, free of THREE and DOM so they can be unit-tested.

export function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

export type Mat3 = number[][];

export interface Eigen3 {
  /** Eigenvalues, in the same order as `vectors`. */
  values: [number, number, number];
  /** Eigenvectors as [x, y, z] columns, aligned with `values`. */
  vectors: [[number, number, number], [number, number, number], [number, number, number]];
}

/** Eigen-decomposition of a symmetric 3x3 matrix via classic Jacobi rotations. */
export function jacobiEigen3(a: Mat3): Eigen3 {
  const A = [a[0].slice(), a[1].slice(), a[2].slice()];
  const V = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1]
  ];
  for (let sweep = 0; sweep < 60; sweep++) {
    let p = 0;
    let q = 1;
    let maxVal = Math.abs(A[0][1]);
    if (Math.abs(A[0][2]) > maxVal) { p = 0; q = 2; maxVal = Math.abs(A[0][2]); }
    if (Math.abs(A[1][2]) > maxVal) { p = 1; q = 2; maxVal = Math.abs(A[1][2]); }
    if (maxVal < 1e-12) break;
    const app = A[p][p], aqq = A[q][q], apq = A[p][q];
    const phi = 0.5 * Math.atan2(2 * apq, aqq - app);
    const c = Math.cos(phi), s = Math.sin(phi);
    for (let i = 0; i < 3; i++) { const aip = A[i][p], aiq = A[i][q]; A[i][p] = c * aip - s * aiq; A[i][q] = s * aip + c * aiq; }
    for (let i = 0; i < 3; i++) { const api = A[p][i], aqi = A[q][i]; A[p][i] = c * api - s * aqi; A[q][i] = s * api + c * aqi; }
    for (let i = 0; i < 3; i++) { const vip = V[i][p], viq = V[i][q]; V[i][p] = c * vip - s * viq; V[i][q] = s * vip + c * viq; }
  }
  return {
    values: [A[0][0], A[1][1], A[2][2]],
    vectors: [
      [V[0][0], V[1][0], V[2][0]],
      [V[0][1], V[1][1], V[2][1]],
      [V[0][2], V[1][2], V[2][2]]
    ]
  };
}

/** Cramer's rule; returns null for a (near-)singular system. */
export function solve3x3(A: Mat3, b: number[]): [number, number, number] | null {
  function det3(M: Mat3): number {
    return M[0][0] * (M[1][1] * M[2][2] - M[1][2] * M[2][1])
         - M[0][1] * (M[1][0] * M[2][2] - M[1][2] * M[2][0])
         + M[0][2] * (M[1][0] * M[2][1] - M[1][1] * M[2][0]);
  }
  const det = det3(A);
  if (Math.abs(det) < 1e-9) return null;
  const cols = [0, 1, 2].map(col => {
    const M = A.map(r => r.slice());
    for (let i = 0; i < 3; i++) M[i][col] = b[i];
    return det3(M) / det;
  });
  return [cols[0], cols[1], cols[2]];
}

export interface Point2D { x: number; y: number; }
export interface CircleFit { cx: number; cy: number; radius: number; }

/** Algebraic (Kåsa) least-squares circle fit: x²+y²+Dx+Ey+F=0 */
export function fitCircle2D(points2D: readonly Point2D[]): CircleFit | null {
  let Sxx = 0, Sxy = 0, Syy = 0, Sx = 0, Sy = 0, Sxz = 0, Syz = 0, Sz = 0;
  const n = points2D.length;
  points2D.forEach(p => {
    const z = p.x * p.x + p.y * p.y;
    Sxx += p.x * p.x; Sxy += p.x * p.y; Syy += p.y * p.y;
    Sx += p.x; Sy += p.y;
    Sxz += p.x * z; Syz += p.y * z; Sz += z;
  });
  const sol = solve3x3([[Sxx, Sxy, Sx], [Sxy, Syy, Sy], [Sx, Sy, n]], [-Sxz, -Syz, -Sz]);
  if (!sol) return null;
  const [D, E, F] = sol;
  const cx = -D / 2, cy = -E / 2;
  const r2 = cx * cx + cy * cy - F;
  return r2 > 0 ? { cx, cy, radius: Math.sqrt(r2) } : null;
}

// ---------------------------------------------------------------------------
// Measure geometry (plain tuples so tests stay THREE-free)
// ---------------------------------------------------------------------------

export type Vec3Tuple = readonly [number, number, number];

function sub(a: Vec3Tuple, b: Vec3Tuple): [number, number, number] {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}
function add(a: Vec3Tuple, b: Vec3Tuple): [number, number, number] {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}
function scale(a: Vec3Tuple, s: number): [number, number, number] {
  return [a[0] * s, a[1] * s, a[2] * s];
}
function dot(a: Vec3Tuple, b: Vec3Tuple): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function len(a: Vec3Tuple): number {
  return Math.hypot(a[0], a[1], a[2]);
}
function norm(a: Vec3Tuple): [number, number, number] {
  const L = len(a);
  return L > 1e-14 ? scale(a, 1 / L) : [0, 0, 0];
}

/** Absolute distance from a point to a plane (point + unit normal). */
export function pointPlaneDistance(point: Vec3Tuple, planePoint: Vec3Tuple, planeNormal: Vec3Tuple): number {
  const n = norm(planeNormal);
  return Math.abs(dot(sub(point, planePoint), n));
}

export interface AxisAxisResult {
  distance: number;
  parallel: boolean;
  closest1: [number, number, number];
  closest2: [number, number, number];
}

/**
 * Closest points between two (infinite) axes. Directions need not be unit;
 * parallel axes use the point-line distance from p1 onto axis 2.
 */
export function closestPointsOnAxes(
  p1: Vec3Tuple, d1: Vec3Tuple,
  p2: Vec3Tuple, d2: Vec3Tuple
): AxisAxisResult {
  const u = norm(d1);
  const v = norm(d2);
  const w0 = sub(p1, p2);
  const a = dot(u, u); // ~1
  const b = dot(u, v);
  const c = dot(v, v); // ~1
  const d = dot(u, w0);
  const e = dot(v, w0);
  const denom = a * c - b * b;

  if (Math.abs(denom) < 1e-10) {
    // Parallel: drop a perpendicular from p1 onto axis 2.
    const t = e; // since |v|=1, param on axis2 for projection of p1
    const closest2 = add(p2, scale(v, t));
    const closest1: [number, number, number] = [p1[0], p1[1], p1[2]];
    return { distance: len(sub(closest1, closest2)), parallel: true, closest1, closest2 };
  }

  const sc = (b * e - c * d) / denom;
  const tc = (a * e - b * d) / denom;
  const closest1 = add(p1, scale(u, sc));
  const closest2 = add(p2, scale(v, tc));
  return { distance: len(sub(closest1, closest2)), parallel: false, closest1, closest2 };
}

/** Acute angle in degrees between two directions (0–90). */
export function acuteAngleDeg(a: Vec3Tuple, b: Vec3Tuple): number {
  const c = clamp(Math.abs(dot(norm(a), norm(b))), 0, 1);
  return Math.acos(c) * (180 / Math.PI);
}
